import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import { sb } from '../supabase';
import BrandedLoading from '../components/BrandedLoading';
import PurchaseRequestForm from '../components/PurchaseRequestForm';
import ProductGallery from '../components/product/ProductGallery';
import SellerStrip from '../components/product/SellerStrip';
import SpecRows from '../components/product/SpecRows';
import ProductChips from '../components/ProductChips';
import NegotiablePill from '../components/NegotiablePill';
import { buildDisplayPrice } from '../lib/displayCurrency';
import { buildProductSpecs, getProductDisplayImages } from '../lib/productMedia';
import { isSupplierPubliclyVisible } from '../lib/supplierOnboarding';
import { attachSupplierProfiles, fetchSupplierPublicProfileById } from '../lib/profileVisibility';
import { PRODUCT_TIER_EMBED, deriveProductPriceFrom } from '../lib/productPriceLookup';
import { buildProductRef } from '../lib/factoryChat';
import { logProductEvent } from '../lib/productEvents';
import { T } from '../lib/supplierDashboardConstants';
import { productPageCopy, moqText } from '../lib/productPageCopy';
import { TrustStrip, ClampText } from '../components/product/ProductPageParts';
import AdminFeatureStar from '../components/product/AdminFeatureStar';

// Self-registered supplier's product page.
//   /products/:id
//
// Deliberately the same page as the factory catalog product page
// (FactoryProductDetail) — same shared components, same order, same spacing,
// gallery down to the two CTAs and nothing after them. A buyer arriving from
// the unified catalog grid should not be able to tell which of the two product
// systems a product came from.
//
// The ONLY difference is what the CTAs do. The factory CTA opens a MANAGED
// request (masked identity, Maabar brokers, factory reached by invite email);
// supplier trade is direct, so:
//   • "request a quote"      -> PurchaseRequestForm -> a `requests` row addressed
//                               to the supplier + a [request:ID] card in chat,
//                               which the supplier answers with an invoice.
//   • "message the supplier" -> /chat/:supplierId
//
// Order in the info column (owner, 2026-10-03, same as the app): name, price
// ("من X ر.س / قطعة"), MOQ → trust strip → details (filled rows only) →
// description (2 lines + اقرأ المزيد) → supplier strip → the two CTAs. No row
// prints "—"; a field without data is not rendered.
//
// This page renders NO tier tables, variant pickers, shipping cards, certificate
// blocks or sample forms. That data still exists on the row and still drives the
// purchase-request and invoice flow; it is simply not what a buyer needs in
// order to ask for a price.

function getLocalizedText(product, lang, key) {
  if (key === 'name') {
    if (lang === 'ar') return product.name_ar || product.name_en || product.name_zh;
    if (lang === 'zh') return product.name_zh || product.name_en || product.name_ar;
    return product.name_en || product.name_zh || product.name_ar;
  }
  if (key === 'desc') {
    if (lang === 'ar') return product.desc_ar || product.desc_en || product.desc_zh;
    if (lang === 'zh') return product.desc_zh || product.desc_en || product.desc_ar;
    return product.desc_en || product.desc_zh || product.desc_ar;
  }
  return '';
}

export default function ProductDetail({ lang = 'ar', user, profile, displayCurrency, exchangeRates }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const arc = isAr ? ' ar' : '';
  const tT = T[lang] || T.en;
  const pc = productPageCopy(lang);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [msgBusy, setMsgBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Public read first. A supplier viewing their OWN product falls through to
      // the owner read below, which is the only way an inactive/draft row is
      // visible at all.
      const { data: baseProduct } = await sb.from('products')
        .select(`*, ${PRODUCT_TIER_EMBED}`).eq('id', id).maybeSingle();
      if (baseProduct) {
        const [withSupplier] = await attachSupplierProfiles(sb, [baseProduct], 'supplier_id', 'profiles');
        // The directory read is the VISIBILITY gate and stays exactly as it was.
        // supplier_public_profiles is then merged on top purely for display
        // columns the directory view does not carry (certifications,
        // business_type) — it is narrower (it requires >=1 active product), so
        // gating on it would hide products that render today. A miss just means
        // no chips.
        if (withSupplier?.profiles) {
          const publicProfile = await fetchSupplierPublicProfileById(sb, withSupplier.supplier_id || withSupplier.profiles.id);
          if (!cancelled) {
            setProduct({ ...withSupplier, profiles: { ...withSupplier.profiles, ...(publicProfile || {}) } });
            setLoading(false);
          }
          return;
        }
      }
      if (profile?.role === 'supplier' && user?.id) {
        const { data: own } = await sb.from('products')
          .select(`*, ${PRODUCT_TIER_EMBED}`).eq('id', id).eq('supplier_id', user.id).maybeSingle();
        if (own) {
          const ownProfile = await fetchSupplierPublicProfileById(sb, user.id);
          if (!cancelled) setProduct({ ...own, profiles: ownProfile || null });
        } else if (!cancelled) {
          setProduct(null);
        }
      } else if (!cancelled) {
        setProduct(null);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, profile?.role, user?.id]);

  // Engagement signal feeding browse_products' popularity score. Until the
  // product_events FK was widened to both catalog sources this page could not
  // log at all, so every supplier product ranked at popularity 0 against
  // factory products that do accumulate one.
  //
  // A supplier opening their OWN product is not demand and must not inflate its
  // rank, so it is not logged. Fire-and-forget: logProductEvent never throws and
  // never blocks the render.
  useEffect(() => {
    if (!product?.id) return;
    if (user?.id && product.supplier_id === user.id) return;
    logProductEvent(product, 'view');
  }, [product, user?.id]);

  // Direct buyer -> supplier chat. Pure navigation: the `messages` stack keys on
  // (sender_id, receiver_id), so there is no thread row to create first. The
  // product rides along in route state and Chat.jsx attaches it to the first
  // message, mirroring the factory page's "message the factory".
  function handleMessageSupplier(supplierId) {
    if (!user) { nav('/login/buyer'); return; }
    if (msgBusy || !supplierId) return;
    setMsgBusy(true);
    logProductEvent(product, 'chat');
    nav(`/chat/${supplierId}`, { state: { product: buildProductRef(product) } });
  }

  if (loading) {
    return <div className="full-page"><div className="fx-wrap"><BrandedLoading lang={lang} tone="product" /></div></div>;
  }
  if (!product) {
    return (
      <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="fx-wrap">
          <button className={`fx-back${arc}`} onClick={() => nav('/products')}>{isAr ? '→ ' : '← '}{tT.pdBackBtn}</button>
          <p style={{ color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{tT.pdProductNotFound}</p>
        </div>
        <Footer lang={lang} />
      </div>
    );
  }

  const sup = product.profiles || {};
  const supplierId = sup.id || product.supplier_id || '';
  const productName = getLocalizedText(product, lang, 'name') || '';
  const descr = getLocalizedText(product, lang, 'desc') || '';
  const supplierName = (sup.company_name_latin || '').trim() || sup.company_name || '';
  const supplierLoc = [sup.city, sup.country].filter(Boolean).join(isAr ? '، ' : ', ');
  const isReviewedSupplier = isSupplierPubliclyVisible(sup.status);
  const gallery = getProductDisplayImages(product);

  // Structured spec fields, already stripped of blanks by buildProductSpecs.
  // Every row here is omitted when the supplier left it empty — unlike the
  // catalog page, where a blank spec means "the PDF did not say" and earns a
  // "contact the supplier" line instead.
  const productSpecs = buildProductSpecs(product, lang);
  const attributes = Array.isArray(product.attributes)
    ? product.attributes.filter((a) => a && a.name && Array.isArray(a.values) && a.values.length)
    : [];

  // ProductChips was written against a factory row. Map the supplier's
  // equivalents the same way catalog_products_public does: business_type
  // 'OEM/ODM' is the private-label signal, certifications carry across, and the
  // category lives on the product rather than the seller.
  const sellerForChips = {
    certifications: sup.certifications,
    private_label: sup.business_type === 'OEM/ODM',
    category: product.category,
  };
  const productForChips = {
    ...product,
    customization_options: product.spec_customization ? [{ ar: product.spec_customization, en: product.spec_customization }] : [],
  };

  const priceFrom = deriveProductPriceFrom(product);
  const price = priceFrom ? buildDisplayPrice({
    amount: priceFrom,
    sourceCurrency: product.currency || 'USD',
    displayCurrency: displayCurrency || product.currency || 'USD',
    rates: exchangeRates,
    lang,
  }) : null;

  return (
    <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fx-wrap">
        <button className={`fx-back${arc}`} onClick={() => nav('/products')}>{isAr ? '→ ' : '← '}{tT.pdBackBtn}</button>

        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8 }}>
          {/* ── Gallery ── */}
          <div style={{ flex: '1 1 380px', minWidth: 280 }}>
            <ProductGallery images={gallery} alt={productName} zoomTitle={tT.pdZoomHint} />
          </div>

          {/* ── Info + request ── */}
          <div style={{ flex: '1 1 360px', minWidth: 280 }}>
            <AdminFeatureStar profile={profile} source="supplier" productId={product.id} lang={lang} />
            {productName && <h1 className={`fx-h1${arc}`} style={{ fontSize: isAr ? 24 : 28, marginBottom: 10 }}>{productName}</h1>}

            {/* Price + MOQ right under the name. Price is the lowest
                product-level tier; with no tiers priced it reads "on request". */}
            <p className={`fx-card-meta${arc}`} style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--text-primary)', fontWeight: 500 }}>
              {price ? pc.priceFrom(isAr ? price.formattedDisplay.replace(/ SAR$/, ' ر.س') : price.formattedDisplay) : pc.priceOnRequest}
            </p>
            {/* The supplier's own-currency price is for the SUPPLIER only: traders
                and visitors see SAR alone (owner, 2026-09-29 — a USD figure next
                to the SAR one reveals the internal rate). */}
            {price && price.isConverted && String(profile?.role || '').toLowerCase() === 'supplier' && (
              <p className={`fx-card-meta${arc}`} style={{ margin: '0 0 6px', fontSize: 11.5 }}>{tT.pdOriginalPriceFn(price.formattedSource)}</p>
            )}
            {price && <div style={{ margin: '6px 0 0' }}><NegotiablePill lang={lang} long /></div>}
            {moqText(product.moq, lang) && (
              <p className={`fx-card-meta${arc}`} style={{ margin: '6px 0 0', fontSize: 13 }}>{moqText(product.moq, lang)}</p>
            )}
            <div style={{ height: 14 }} />

            <TrustStrip lang={lang} verified={isReviewedSupplier} />

            <ProductChips product={productForChips} factory={sellerForChips} lang={lang} style={{ margin: '0 0 16px' }} />

            {/* Details, then description. emptyMode="omit" — the supplier filled
                this form themselves, so a blank field means they chose not to
                state it and the row goes. */}
            <SpecRows
              lang={lang}
              emptyMode="omit"
              rows={[
                ...productSpecs.map((s) => ({ key: s.key, label: s.label, value: s.value })),
                ...(product.sample_available ? [{ key: 'samples', label: pc.samples, value: pc.samplesAvailable }] : []),
                ...(product.country_of_origin ? [{ key: 'origin', label: pc.origin, value: pc.country(product.country_of_origin) }] : []),
                ...(attributes.length ? [{
                  key: 'attrs',
                  label: tT.pdCustomizationLabel,
                  children: (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {attributes.flatMap((attr, i) => attr.values.map((val, j) => (
                        <span key={`${i}-${j}`} style={{ padding: '3px 11px', borderRadius: 20, background: 'var(--bg-hero)', fontSize: 12, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{`${attr.name}: ${val}`}</span>
                      )))}
                    </div>
                  ),
                }] : []),
                ...(descr ? [{
                  key: 'desc',
                  label: tT.pdDescLabel,
                  children: <ClampText lang={lang} text={descr} style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} />,
                }] : []),
              ]}
            />

            {supplierId && (
              <SellerStrip
                to={`/supplier/${supplierId}`}
                image={sup.avatar_url}
                kicker={tT.pdSupplierLabel}
                name={supplierName}
                meta={supplierLoc}
                verified={isReviewedSupplier}
                verifiedLabel={tT.pdVerifiedSupplierPill}
                rating={sup.rating}
                ctaLabel={pc.seeFactory}
                lang={lang}
              />
            )}
            <div style={{ height: 6 }} />

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className={`fx-btn-primary${arc}`} onClick={() => {
                if (!user) { nav('/login/buyer'); return; }
                logProductEvent(product, 'quote');
                setReqOpen(true);
              }}>{pc.requestQuote}</button>
              <button className={`fx-btn-ghost${arc}`} onClick={() => handleMessageSupplier(supplierId)} disabled={msgBusy || !supplierId}>{msgBusy ? tT.pdMsgStarting : pc.askFactory}</button>
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '10px 0 0', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{tT.pdQuoteNote}</p>
          </div>
        </div>
      </div>

      {reqOpen && supplierId && (
        <PurchaseRequestForm
          product={product}
          lang={lang}
          onClose={() => setReqOpen(false)}
          onSubmitted={() => { setReqOpen(false); nav(`/chat/${supplierId}`); }}
        />
      )}

      <Footer lang={lang} />
    </div>
  );
}
